/* eslint-disable @typescript-eslint/no-unused-vars */
import { DashboardOutlined, LogoutOutlined, UnorderedListOutlined } from '@ant-design/icons';
import {
  CanAccess,
  ITreeMenu,
  useIsExistAuthentication,
  useMenu,
  usePermissions,
  useRefineContext,
  useTitle,
  useTranslate,
} from '@pankod/refine-core';
import { Avatar, Button, Grid, Layout, Menu, Select, Tooltip, Typography } from 'antd';
import React, { useEffect, useState } from 'react';

import { Title as DefaultTitle } from '../title/index';

import { useAuth0 } from '@auth0/auth0-react';
import { RefineLayoutSiderProps } from '@pankod/refine-antd';
import RouterProvider from '@pankod/refine-react-router-v6';
import { useResources, useUserData } from 'hooks';
import {
  RiArrowLeftSLine,
  RiArrowRightSLine,
  RiBuilding2Line,
  RiGitBranchLine,
  RiLogoutBoxLine,
} from 'react-icons/ri';
import { useFeatureFlags } from 'hooks/useFeatureFlags';
import { getAppInstances } from 'services/app-instance';
import { getOrganizations } from 'services/organization';
import useAppInstanceStore from 'store/useAppInstanceStore';
import useOrganizationStore from 'store/useOrganizationStore';
import { isMenuItemVisible } from 'utilities/menu-visibility';
import { antLayoutSider, antLayoutSiderMobile } from './styles';
import styles from './styles.module.css';

const { SubMenu } = Menu;
const { Link } = RouterProvider;
const { Text } = Typography;
const { Option } = Select;

export const Sider: React.FC<RefineLayoutSiderProps> = ({ render }) => {
  const { groupArray } = useResources();
  const [collapsed, setCollapsed] = useState<boolean>(false);
  const isExistAuthentication = useIsExistAuthentication();
  // const { mutate: mutateLogout } = useLogout();
  const { logout: auth0Logout } = useAuth0();
  const Title = useTitle();
  const translate = useTranslate();
  const { menuItems, selectedKey, defaultOpenKeys } = useMenu();
  const breakpoint = Grid.useBreakpoint();
  const { hasDashboard } = useRefineContext();
  const { data: permissionData } = usePermissions();
  const { data: featureFlags } = useFeatureFlags();
  // const permissionData = data?.user;

  const [isLoading, setIsLoading] = useState(false);
  const { appInstances, setAppInstances, currentAppInstance, setCurrentAppInstance } =
    useAppInstanceStore();
  const { organizations, currentOrganization, setOrganizations, setCurrentOrganization } =
    useOrganizationStore();
  const { user, loginWithRedirect } = useAuth0();
  const userData = useUserData();
  const [open, setOpen] = useState(false);
  const onClose = () => setOpen(false);

  const isMobile = breakpoint.lg === false;

  useEffect(() => {
    if (isMobile) {
      setCollapsed(true);
    }
  }, [isMobile]);

  const RenderToTitle = Title ?? DefaultTitle;

  const fetchOrgAndAppInstance = async () => {
    setIsLoading(true);
    try {
      const [organizationResponse, appInstanceResponse] = await Promise.all([
        getOrganizations(),
        getAppInstances(),
      ]);
      setOrganizations(organizationResponse);
      setAppInstances(appInstanceResponse);
    } catch (err: any) {
      console.error(err?.message ?? err ?? 'Failed to fetch org and app instances');
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (!user) return;
    fetchOrgAndAppInstance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (userData.user) {
      const tokenOrgId = userData.user?.organization?.id;
      const tokenAppId = userData.user?.platform?.id;

      if (tokenOrgId) {
        const currentOrganization = organizations.find((org) => org.id === tokenOrgId);
        if (currentOrganization) setCurrentOrganization(currentOrganization);
      }

      if (tokenAppId) {
        const currentAppInstance = appInstances.find((app) => app.id === tokenAppId);
        if (currentAppInstance) setCurrentAppInstance(currentAppInstance);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizations, appInstances, userData.user]);

  const onChangeOrgOption = (id: string) => {
    if (currentOrganization && id === currentOrganization.id) return;

    const redirectUri = `${window.location.origin}/`;
    const selectedOrganization = organizations.find((org) => org.id === id);
    if (selectedOrganization) {
      window.localStorage.clear();
      loginWithRedirect({ orgId: id, redirectUri });
      setCurrentOrganization(selectedOrganization);
    }
  };

  const onChangeAppOption = (id: string) => {
    if (currentAppInstance && id === currentAppInstance.id) return;

    const selectedAppInstance = appInstances.find((app) => app.id === id);
    if (currentOrganization && selectedAppInstance) {
      window.localStorage.clear();
      loginWithRedirect({
        orgId: currentOrganization.id,
        platformId: selectedAppInstance.id,
      });
      setCurrentAppInstance(selectedAppInstance);
    }
  };

  const handleLogout = () => {
    window.localStorage.clear();
    auth0Logout({
      returnTo: window.location.origin,
    });
  };

  const renderTreeView = (tree: ITreeMenu[], selectedKey: string) => {
    return tree.map((item: ITreeMenu) => {
      const { icon, label, route, name, children, parentName } = item;

      if (children.length > 0) {
        return (
          <CanAccess
            key={route}
            resource={name.toLowerCase()}
            action="list"
            params={{
              resource: item,
            }}
          >
            <SubMenu key={route} icon={icon ?? <UnorderedListOutlined />} title={label}>
              {renderTreeView(children, selectedKey)}
            </SubMenu>
          </CanAccess>
        );
      }
      const isSelected = route === selectedKey;
      const isRoute = !(parentName !== undefined && children.length === 0);
      const isMdOrGreater = breakpoint.md;
      return (
        <CanAccess
          key={route}
          resource={name.toLowerCase()}
          action="list"
          params={{
            resource: item,
          }}
        >
          <Menu.Item
            key={route}
            style={{
              color: isSelected ? '#0075ff' : '#6d96b3',
              backgroundColor: isSelected && !collapsed ? '#f9f9fc' : 'transparent',
              borderLeft: isSelected && isMdOrGreater && !collapsed ? '3px solid #0075FF' : 'none',
              borderTopRightRadius: '8px',
              borderBottomRightRadius: '8px',
              transition: 'all 0.3s ease',
            }}
            icon={icon ?? (isRoute && <UnorderedListOutlined />)}
          >
            <Link
              to={route ?? ''}
              className={isSelected ? styles['menu-items-selected'] : styles['menu-items']}
            >
              {label}
            </Link>
            {!collapsed && isSelected && <div className="ant-menu-tree-arrow" />}
          </Menu.Item>
        </CanAccess>
      );
    });
  };

  const logout = isExistAuthentication && (
    <Menu.Item
      key="logout"
      // onClick={() => mutateLogout()}
      onClick={handleLogout}
      icon={<LogoutOutlined color="#6d96b3" />}
      className={styles['menu-items']}
    >
      Logout
    </Menu.Item>
  );

  const dashboard = hasDashboard ? (
    <Menu.Item
      key="dashboard"
      style={{
        fontWeight: selectedKey === '/' ? 'bold' : 'normal',
      }}
      icon={<DashboardOutlined />}
    >
      <Link to="/">{translate('dashboard.title', 'Dashboard')}</Link>
      {!collapsed && selectedKey === '/' && <div className="ant-menu-tree-arrow" />}
    </Menu.Item>
  ) : null;

  const items = renderTreeView(menuItems, selectedKey);

  const renderSider = () => {
    if (render) {
      return render({
        dashboard,
        items,
        logout,
        collapsed,
      });
    }
    return (
      <>
        {/* {dashboard}
        {items}
        {logout} */}
        {nestedMenuRender()}
      </>
    );
  };

  const renderUserInfo = () => {
    return (
      <Menu.SubMenu
        key="sub1"
        title={
          <div
            style={{
              paddingLeft: 0,
              display: 'flex',
              gap: 8,
              alignItems: 'center',
            }}
          >
            <div>
              <Avatar
                size={32}
                src={userData.user?.picture}
                alt={userData.user?.name}
                style={{ backgroundColor: '#F1F6FD' }}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <Tooltip title={userData.user?.name}>
                <Text style={{ color: '#6d96b3', maxHeight: 20 }}>{userData.user?.name}</Text>
              </Tooltip>
              <div>
                <Tooltip title={currentAppInstance?.displayName}>
                  <Text style={{ color: '#6d96b3' }}>{currentAppInstance?.displayName}</Text>
                </Tooltip>
              </div>
            </div>
          </div>
        }
        theme="light"
        style={{ padding: 0 }}
      >
        <Menu.Item
          style={{
            padding: '0 16px',
            margin: 0,
            borderBottom: 'none',
            backgroundColor: '#FFFFFF',
            color: '#6d96b3',
            fontWeight: 500,
          }}
        >
          Company
        </Menu.Item>
        <div
          style={{
            backgroundColor: '#FFFFFF',
            width: '100%',
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'center',
            padding: '0 16px',
          }}
        >
          <Select
            loading={isLoading}
            suffixIcon={<RiBuilding2Line color="#0075ff" />}
            placeholder="Select Org"
            value={currentOrganization?.id}
            style={{
              minWidth: '100%',
              backgroundColor: '#F1F6FD',
              color: '#6d96b3',
              boxShadow: 'none',
            }}
            onChange={(id: string) => onChangeOrgOption(id)}
            disabled={isLoading}
            getPopupContainer={(trigger) => trigger.parentElement}
          >
            {organizations.map((org) => (
              <Option key={org.id} value={org.id}>
                {org.displayName}
              </Option>
            ))}
          </Select>
        </div>

        <Menu.Item
          style={{
            padding: '0 16px',
            backgroundColor: '#FFFFFF',
            color: '#6d96b3',
            margin: 0,
            borderBottom: 'none',
            fontWeight: 500,
          }}
        >
          Concern
        </Menu.Item>

        <div
          style={{
            backgroundColor: '#FFFFFF',
            width: '100%',
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'center',
            padding: '0 16px',
          }}
        >
          <Select
            id="test"
            loading={isLoading}
            suffixIcon={<RiGitBranchLine color="#0075ff" />}
            placeholder="Select App"
            value={currentAppInstance?.id}
            style={{
              minWidth: '100%',
              backgroundColor: '#F1F6FD',
              color: '#6d96b3',
              boxShadow: 'none',
            }}
            onChange={(id: string) => onChangeAppOption(id)}
            disabled={isLoading}
            getPopupContainer={(trigger) => trigger.parentElement}
          >
            {appInstances.map((app) => (
              <Option key={app.id} value={app.id}>
                {app.displayName}
              </Option>
            ))}
          </Select>
        </div>
        <Menu.Item
          style={{
            padding: '32px 16px 48px 16px',
            backgroundColor: '#FFFFFF',
            margin: 0,
          }}
        >
          {isExistAuthentication && (
            <Button
              // onClick={() => mutateLogout()}
              onClick={handleLogout}
              icon={<RiLogoutBoxLine />}
              title="Logout"
              style={{
                minWidth: '100%',
                backgroundColor: '#426dea',
                color: 'white',
                border: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 4,
              }}
              type="primary"
            >
              {!collapsed ? 'Logout' : null}
            </Button>
          )}
        </Menu.Item>
      </Menu.SubMenu>
    );
  };

  const nestedMenuRender = () => {
    const getSelectedKey = selectedKey ? [`/${selectedKey.split('/')[1]}`] : [];

    return (
      <Menu
        selectedKeys={getSelectedKey}
        mode="inline"
        theme="light"
        defaultSelectedKeys={['1']}
        defaultOpenKeys={['sub1']}
        style={{
          paddingTop: '8px',
          border: 'none',
          overflowY: isMobile ? 'auto' : undefined,
          overflowX: isMobile ? 'hidden' : undefined,
          height: isMobile ? '100%' : undefined,
          minHeight: isMobile ? 0 : undefined,
          paddingBottom: isMobile ? 'max(12px, env(safe-area-inset-bottom))' : 0,
          scrollbarWidth: isMobile ? 'none' : undefined,
        }}
        onClick={() => {
          if (isMobile) {
            setCollapsed(true);
          }
        }}
      >
        {Object.keys(groupArray).map((group, index) => {
          // Collect items under the group
          const items = groupArray[group]
            .map((menuItem) => {
              const findMenuItems = menuItems.find((element) => element.name === menuItem);
              if (!findMenuItems) return null;

              const { label } = findMenuItems;
              const isVisible = isMenuItemVisible({
                menuItem,
                permissionData,
                featureFlags,
                requiredFeatureFlag: findMenuItems.options?.featureFlag,
              });

              if (!isVisible) return null;

              return label; // Return the label of the menu item
            })
            .filter(Boolean); // Filter out null or undefined items

          // Skip rendering the group if no items
          if (items.length === 0) {
            return null;
          }

          return (
            <Menu.ItemGroup
              key={group}
              style={{
                marginBottom: `${index !== Object.keys(groupArray).length - 1 ? '16px' : '0px'}`,
              }}
              title={
                <span
                  style={{
                    fontSize: '12px',
                    fontWeight: '500',
                    lineHeight: '14.06px',
                    letterSpacing: '2%',
                    color: '#9EA5AD',
                    textTransform: 'uppercase',
                  }}
                >
                  {group}
                </span>
              }
            >
              {groupArray[group].map((menuItem) => {
                const findMenuItems = menuItems.find((element) => element.name === menuItem);

                if (!findMenuItems) {
                  return null; // Skip rendering if findMenuItems is null or undefined
                }

                const { icon, route, label } = findMenuItems;
                const displayPosLabel = label === 'Pos' ? 'POS' : label;
                const isSelected = selectedKey === route;
                const isMdOrGreater = breakpoint.md;
                const isVisible = isMenuItemVisible({
                  menuItem,
                  permissionData,
                  featureFlags,
                  requiredFeatureFlag: findMenuItems.options?.featureFlag,
                });

                if (!isVisible) {
                  return null; // Skip rendering without view permission or the screen's flag
                }

                return (
                  <Menu.Item
                    key={route}
                    onClick={() => selectedKey}
                    style={{
                      color: isSelected ? '#0075ff' : '#6d96b3',
                      backgroundColor: isSelected && !collapsed ? '#f9f9fc' : 'transparent',
                      borderLeft:
                        isSelected && isMdOrGreater && !collapsed ? '4px solid #0075FF' : 'none',
                      // borderTopRightRadius: '8px',
                      // borderBottomRightRadius: '8px',
                      transition: 'all 0.3s ease',
                    }}
                    icon={icon}
                    title={isMobile && collapsed ? null : displayPosLabel}
                  >
                    <Link
                      className={isSelected ? styles['menu-items-selected'] : styles['menu-items']}
                      to={route ?? ''}
                    >
                      {!collapsed ? displayPosLabel : null}
                    </Link>
                  </Menu.Item>
                );
              })}
            </Menu.ItemGroup>
          );
        })}
        {!collapsed && renderUserInfo()}
        {/* {logout} */}
      </Menu>
    );
  };

  return (
    <Layout.Sider
      collapsible
      collapsed={collapsed}
      onCollapse={(collapsed: boolean): void => setCollapsed(collapsed)}
      collapsedWidth={isMobile ? 0 : 80}
      breakpoint="lg"
      style={isMobile ? antLayoutSiderMobile : antLayoutSider}
      theme="light"
      trigger={
        <div
          style={{
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          {collapsed ? (
            <RiArrowLeftSLine size={24} color="#6d96b3" />
          ) : (
            <RiArrowRightSLine size={24} color="#6d96b3" />
          )}
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
        <RenderToTitle collapsed={collapsed} />
        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: isMobile ? 'hidden' : 'auto',
            overflowX: 'hidden',
            paddingBottom: isMobile ? 'max(12px, env(safe-area-inset-bottom))' : 56,
            scrollbarWidth: isMobile ? undefined : 'none',
          }}
        >
          {renderSider()}
        </div>
      </div>
    </Layout.Sider>
  );
};
